/**
 * sb-capture.js — StormBag blog email-capture behavior
 * ---------------------------------------------------------------
 * Hand-rolled architecture: theme owns the three capture surfaces
 * (inline card, sticky bar, exit modal), all posting to Shopify's
 * /contact customer endpoint with placement-specific tags.
 *
 * This script handles:
 *   1. Inline card — IntersectionObserver view analytics
 *   2. Sticky bar  — scroll-triggered reveal, dismiss cookie, analytics
 *   3. Exit modal  — mouseout / mobile-timeout trigger, dismiss/submit
 *                    cookies, ESC + backdrop close, focus trap, analytics
 *   4. Post-submit — after Shopify redirects back with ?customer_posted=true,
 *                    reveal the surface whose form.posted_successfully? branch
 *                    rendered the success message, scroll to it, fire the
 *                    conversion event.
 *
 * All behavior fails-open. No dependencies.
 */
(function () {
  'use strict';

  var SCROLL_TRIGGER_PCT = 40;
  var BAR_COOKIE = 'sb_bar_dismissed';
  var BAR_COOKIE_DAYS = 30;
  var EXIT_DISMISSED_COOKIE = 'sb_exit_dismissed';
  var EXIT_DISMISSED_DAYS = 14;
  var EXIT_SUBMITTED_COOKIE = 'sb_exit_submitted';
  var EXIT_SUBMITTED_DAYS = 365;
  var EXIT_MOBILE_TIMEOUT_MS = 45000; // fallback on mobile (no mouseout) — 45s idle
  var EXIT_ARM_DELAY_MS = 5000;       // wait 5s before arming desktop exit-intent

  // ---------------------------------------------------------------
  // Utilities
  // ---------------------------------------------------------------
  function hasCookie(name) {
    return document.cookie.split('; ').some(function (row) {
      return row.indexOf(name + '=') === 0;
    });
  }

  function setCookie(name, value, days) {
    try {
      var d = new Date();
      d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
      document.cookie =
        name + '=' + value + '; expires=' + d.toUTCString() + '; path=/; SameSite=Lax';
    } catch (e) { /* fail-open */ }
  }

  function pushDataLayer(event, placement) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: event, placement: placement });
    } catch (e) { /* fail-open */ }
  }

  function isMobile() {
    return window.matchMedia && window.matchMedia('(max-width: 768px)').matches;
  }

  function observeView(el, placement) {
    if (!el) return;
    if (typeof IntersectionObserver !== 'function') return;
    var fired = false;
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!fired && entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            fired = true;
            pushDataLayer('email_capture_view', placement);
            io.disconnect();
          }
        });
      },
      { threshold: [0, 0.5, 1] }
    );
    io.observe(el);
  }

  // ---------------------------------------------------------------
  // Inline card
  // ---------------------------------------------------------------
  // The section is rendered [hidden] at the bottom of the DOM by
  // Shopify. We move it into the article body (after the 2nd H2
  // of the actual rte/article-content element, not any H2 in the
  // page chrome). Fallback: after the block that sits at ~50% of
  // the article's rendered text.
  function findArticleBody() {
    // Try selectors in specificity order — prefer .rte inside an
    // article element so we don't pick up sidebars, cart drawer,
    // "You may also like" strips, etc.
    var selectors = [
      'article .rte',
      'article .article__content',
      'main article .rte',
      '.article-template__content .rte',
      '.article__content',
      'article'
    ];
    for (var i = 0; i < selectors.length; i++) {
      var el = document.querySelector(selectors[i]);
      if (el && el.textContent && el.textContent.trim().length > 400) return el;
    }
    return null;
  }

  function findInjectionTarget(articleBody) {
    if (!articleBody) return null;
    // Prefer: after the 2nd H2 inside the article body
    var h2s = articleBody.querySelectorAll(':scope > h2, :scope > * h2');
    // Filter to top-level or one-deep H2s (avoid H2s inside blockquotes/asides)
    var directH2s = [];
    for (var i = 0; i < h2s.length; i++) {
      var h = h2s[i];
      // Only accept if nearest article-body ancestor is this articleBody
      var closest = h.closest('.rte, .article__content, article');
      if (closest === articleBody) directH2s.push(h);
    }
    if (directH2s.length >= 2) return directH2s[1];
    if (directH2s.length === 1) return directH2s[0];

    // Fallback: pick the block whose accumulated text puts us near 50%
    var blocks = articleBody.querySelectorAll(':scope > p, :scope > h2, :scope > h3, :scope > ul, :scope > ol');
    if (!blocks.length) return null;
    var total = 0;
    for (var j = 0; j < blocks.length; j++) total += (blocks[j].textContent || '').length;
    if (total < 400) return null;
    var target = total * 0.5;
    var acc = 0;
    for (var k = 0; k < blocks.length; k++) {
      acc += (blocks[k].textContent || '').length;
      if (acc >= target) return blocks[k];
    }
    return blocks[blocks.length - 1];
  }

  function initInline() {
    var inline = document.querySelector('[data-sb-capture="inline"]');
    if (!inline) return;

    // Emergency-hardening: force display:none inline so stale cached CSS
    // can't override [hidden] and flash the card at the wrong DOM position.
    inline.style.display = 'none';

    // Only relocate on article pages; on blog index it wouldn't be rendered.
    var body = findArticleBody();
    var target = findInjectionTarget(body);

    if (target && target.parentNode) {
      // Insert AFTER the target block
      target.parentNode.insertBefore(inline, target.nextSibling);
    } else if (body) {
      // No good target — append at end of article body
      body.appendChild(inline);
    }

    inline.hidden = false;
    inline.style.display = '';

    observeView(inline, 'inline');

    // Track submit click for conversion analytics
    var submitBtn = inline.querySelector('[data-sb-capture-submit="inline"]');
    if (submitBtn) {
      submitBtn.addEventListener('click', function () {
        pushDataLayer('email_capture_submit', 'inline');
      });
    }
  }

  // ---------------------------------------------------------------
  // Sticky bar — scroll reveal + dismiss cookie
  // ---------------------------------------------------------------
  function initStickyBar() {
    var bar = document.querySelector('[data-sb-capture="bar"]');
    if (!bar) return;

    // Emergency-hardening: force display:none inline so stale cached CSS
    // can't override [hidden].
    bar.style.display = 'none';

    if (hasCookie(BAR_COOKIE)) {
      bar.parentNode && bar.parentNode.removeChild(bar);
      return;
    }

    var shown = false;
    var footerHidden = false;

    // How close to the bottom of the page (in px) the bar should hide
    // itself so it stops overlapping the site footer. Roughly the height
    // of the mobile footer.
    var FOOTER_HIDE_PX = 480;

    function maybeShow() {
      var scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
      var scrollHeight = document.documentElement.scrollHeight || 0;
      var clientHeight = document.documentElement.clientHeight || 0;
      var docHeight = scrollHeight - clientHeight;
      if (docHeight <= 0) return;
      var pct = (scrollTop / docHeight) * 100;
      var distanceToBottom = docHeight - scrollTop;

      if (!shown && pct >= SCROLL_TRIGGER_PCT) {
        shown = true;
        bar.hidden = false;
        bar.style.display = '';
        // eslint-disable-next-line no-unused-expressions
        bar.offsetHeight; // force reflow
        bar.classList.add('sb-sticky-bar--visible');
        pushDataLayer('email_capture_view', 'bar');
      }

      // Once shown, hide the bar as the user nears the footer so it
      // doesn't sit on top of the footer's own email form and copyright
      // line. Re-show it as soon as they scroll back up.
      if (shown) {
        if (distanceToBottom < FOOTER_HIDE_PX && !footerHidden) {
          footerHidden = true;
          bar.classList.remove('sb-sticky-bar--visible');
        } else if (distanceToBottom >= FOOTER_HIDE_PX && footerHidden) {
          footerHidden = false;
          bar.classList.add('sb-sticky-bar--visible');
        }
      }
    }

    var scheduled = false;
    function onScroll() {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(function () {
        scheduled = false;
        maybeShow();
      });
    }

    var dismiss = bar.querySelector('[data-sb-capture-dismiss="bar"]');
    if (dismiss) {
      dismiss.addEventListener('click', function () {
        setCookie(BAR_COOKIE, '1', BAR_COOKIE_DAYS);
        bar.classList.remove('sb-sticky-bar--visible');
        bar.classList.add('sb-sticky-bar--dismissed');
        setTimeout(function () {
          bar.parentNode && bar.parentNode.removeChild(bar);
        }, 350);
      });
    }

    var submitBtn = bar.querySelector('[data-sb-capture-submit="bar"]');
    if (submitBtn) {
      submitBtn.addEventListener('click', function () {
        pushDataLayer('email_capture_submit', 'bar');
      });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    maybeShow();
  }

  // ---------------------------------------------------------------
  // Exit modal — mouseout / mobile-timeout trigger
  // ---------------------------------------------------------------
  function initExitModal() {
    var modal = document.querySelector('[data-sb-capture="exit"]');
    if (!modal) return;

    // Emergency-hardening: force display:none inline so we don't rely on
    // the CSS [hidden] rule (which older cached CSS may not have).
    modal.style.display = 'none';

    // If already submitted or dismissed within window, remove entirely
    if (hasCookie(EXIT_SUBMITTED_COOKIE) || hasCookie(EXIT_DISMISSED_COOKIE)) {
      modal.parentNode && modal.parentNode.removeChild(modal);
      return;
    }

    var shown = false;
    var previouslyFocused = null;

    function show() {
      if (shown) return;
      shown = true;
      previouslyFocused = document.activeElement;
      modal.hidden = false;
      modal.style.display = 'flex';
      // Lock body scroll
      document.body.style.overflow = 'hidden';
      // Focus the email input for keyboard users
      var input = modal.querySelector('input[type="email"]');
      if (input) {
        setTimeout(function () { input.focus(); }, 100);
      }
      pushDataLayer('email_capture_view', 'exit');
      teardownTriggers();
    }

    function dismiss() {
      setCookie(EXIT_DISMISSED_COOKIE, '1', EXIT_DISMISSED_DAYS);
      modal.hidden = true;
      modal.style.display = 'none';
      document.body.style.overflow = '';
      if (previouslyFocused && previouslyFocused.focus) {
        previouslyFocused.focus();
      }
    }

    // Desktop trigger: mouse leaves the TOP of the viewport (exit intent)
    // Guardrails to prevent nuisance fires on page-in / nav-in:
    //   1. armReady    — timer must elapse (15s) before exit-intent arms
    //   2. mouseDeepIn — user's mouse must have been at clientY > 120 at
    //                    some point (meaning they engaged with content,
    //                    not just navigated in with cursor near the top)
    //   3. leaving via top edge (clientY <= 5) with no relatedTarget
    var mouseDeepIn = false;
    var armReady = false;

    function onMouseMove(e) {
      if (e.clientY > 120) {
        mouseDeepIn = true;
        document.removeEventListener('mousemove', onMouseMove);
      }
    }

    function onMouseOut(e) {
      if (!armReady || !mouseDeepIn) return;
      // Ignore mouseout events that stay inside the viewport (child element transitions)
      if (e.relatedTarget || e.toElement) return;
      // Fire only when mouse leaves via top edge
      if (e.clientY > 5) return;
      show();
    }

    // Mobile fallback: after N seconds on page
    var mobileTimer = null;
    var armTimer = null;

    function setupTriggers() {
      if (isMobile()) {
        mobileTimer = setTimeout(show, EXIT_MOBILE_TIMEOUT_MS);
      } else {
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseout', onMouseOut);
        // Require at least EXIT_ARM_DELAY_MS on page before arming exit-intent
        armTimer = setTimeout(function () { armReady = true; }, EXIT_ARM_DELAY_MS);
      }
    }

    function teardownTriggers() {
      if (mobileTimer) { clearTimeout(mobileTimer); mobileTimer = null; }
      if (armTimer) { clearTimeout(armTimer); armTimer = null; }
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseout', onMouseOut);
    }

    // Dismiss handlers (× button + backdrop click)
    modal.querySelectorAll('[data-sb-capture-dismiss="exit"]').forEach(function (el) {
      el.addEventListener('click', dismiss);
    });

    // ESC to close
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !modal.hidden) {
        dismiss();
      }
    });

    // Submit click analytics
    var submitBtn = modal.querySelector('[data-sb-capture-submit="exit"]');
    if (submitBtn) {
      submitBtn.addEventListener('click', function () {
        pushDataLayer('email_capture_submit', 'exit');
        // Mark as submitted so we don't nag again for a year
        setCookie(EXIT_SUBMITTED_COOKIE, '1', EXIT_SUBMITTED_DAYS);
      });
    }

    setupTriggers();
  }

  // ---------------------------------------------------------------
  // Post-submit success handling
  // ---------------------------------------------------------------
  // On ?customer_posted=true, Shopify's server rendered the
  // `form.posted_successfully?` branch inside whichever surface
  // was submitted. Find that surface, force it visible (bypassing
  // normal scroll/exit-intent triggers), scroll it into view,
  // and fire the conversion analytics event.
  //
  // Returns the placement string if a success surface was handled,
  // or null if none was found (so the normal initializers can run).
  function handlePostSubmit() {
    if (window.location.search.indexOf('customer_posted=true') === -1) return null;

    var successEl = document.querySelector('[data-sb-capture-success]');
    if (!successEl) return null;

    var placement = successEl.getAttribute('data-sb-capture-success') || 'unknown';
    var surface = successEl.closest('[data-sb-capture]');
    if (!surface) {
      pushDataLayer('email_capture_success', placement);
      return placement;
    }

    // Force the surface visible regardless of normal gating.
    surface.hidden = false;
    surface.style.display = '';

    if (placement === 'inline') {
      // Relocate into article body so it appears in-context, then
      // reveal it. This mirrors initInline() but skips the observe/
      // submit-tracking wiring — we've already submitted.
      var body = findArticleBody();
      var target = findInjectionTarget(body);
      if (target && target.parentNode) {
        target.parentNode.insertBefore(surface, target.nextSibling);
      } else if (body) {
        body.appendChild(surface);
      }
      surface.hidden = false;
      surface.style.display = '';
    } else if (placement === 'bar') {
      // Reveal the sticky bar and force it into the visible state.
      // eslint-disable-next-line no-unused-expressions
      surface.offsetHeight; // reflow
      surface.classList.add('sb-sticky-bar--visible');
    } else if (placement === 'exit') {
      // Reveal the modal on top of the page.
      surface.style.display = 'flex';
      document.body.style.overflow = 'hidden';
      // Wire dismiss handlers so the user can close the confirmation.
      surface.querySelectorAll('[data-sb-capture-dismiss="exit"]').forEach(function (el) {
        el.addEventListener('click', function () {
          surface.hidden = true;
          surface.style.display = 'none';
          document.body.style.overflow = '';
        });
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !surface.hidden) {
          surface.hidden = true;
          surface.style.display = 'none';
          document.body.style.overflow = '';
        }
      });
      // Suppress future exit-modal nagging for a year.
      setCookie(EXIT_SUBMITTED_COOKIE, '1', EXIT_SUBMITTED_DAYS);
    }

    // Scroll to the success confirmation for placements that live in
    // the page flow. (Exit modal is fixed, so scroll is unnecessary.)
    if (placement === 'inline' || placement === 'bar') {
      try {
        successEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (e) { /* fail-open */ }
    }

    pushDataLayer('email_capture_success', placement);
    return placement;
  }

  // ---------------------------------------------------------------
  // Boot
  // ---------------------------------------------------------------
  function boot() {
    // On success page: skip the normal init for whichever surface
    // rendered the success branch (handlePostSubmit already revealed it).
    // Other surfaces don't render post-submit anyway (only one form was
    // submitted, and Liquid only rendered the success branch inside it),
    // but their init calls are safe no-ops if the surface isn't present.
    var successPlacement = handlePostSubmit();

    if (successPlacement !== 'inline') initInline();
    if (successPlacement !== 'bar') initStickyBar();
    if (successPlacement !== 'exit') initExitModal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();

